/**
 * Admin V3 — usePdfDownload hook
 *
 * Authenticated blob download for server-rendered PDFs (Facture / Avoir).
 * Mirrors the DocumentViewer blob pattern (getAuthHeaders + responseType:'blob'
 * + createObjectURL), but triggers a file download via a transient <a download>
 * instead of an inline viewer.
 *
 * GOVERNANCE: read-only. Hits GET …/pdf endpoints (ADMIN_PARTNER_READ). The
 * backend gates these on CANONICAL_INVOICE_PDF_ENABLED (501 while OFF) and the
 * G3 company identity (409 if unset); this hook surfaces those as `error`.
 *
 * API:
 *   const { download, loading, error } = usePdfDownload();
 *   await download('/admin/billing/invoices/<id>/pdf', 'F-2026-000001.pdf');
 */

import { useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';

const API_URL = import.meta.env.VITE_BACKEND_URL + '/api';

const usePdfDownload = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const download = useCallback(async (endpoint, filename = 'document.pdf') => {
    setLoading(true);
    setError(null);
    let url;
    try {
      const res = await axios.get(`${API_URL}${endpoint}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      return true;
    } catch (err) {
      // PDF endpoints can return 501 (flag OFF) / 409 (G3 unset) as a blob;
      // expose a readable message either way.
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err?.message || 'Téléchargement impossible'));
      return false;
    } finally {
      if (url) URL.revokeObjectURL(url);
      setLoading(false);
    }
  }, [getAuthHeaders]);

  return { download, loading, error };
};

export default usePdfDownload;
